import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Swords, RefreshCw } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (utc) => {
  try {
    return new Date(utc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET';
  } catch { return ''; }
};

const NhlMatchups = () => {
  const [games, setGames] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/nhl/matchups`);
      setGames(res.data.games || []);
      setDate(res.data.date || '');
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Swords className="h-8 w-8 text-orange-400" />
                NHL Matchups
              </h1>
              <p className="text-muted-foreground mt-1">Scores, odds, team form, goaltenders & head-to-head history {date && `• ${date}`}</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-nhl-matchups-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-56 skeleton rounded-lg" />)}</div>
          ) : games.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No NHL games on the schedule right now.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {games.map((g, i) => (
                <Card key={g.game_id || i} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`nhl-game-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                      <div>
                        <div className="font-heading text-xl font-bold text-white uppercase">
                          {g.away_name} @ {g.home_name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {g.is_final ? 'Final' : g.is_live ? `P${g.period} • ${g.clock || ''}` : fmtTime(g.start_utc)} {g.venue && `• ${g.venue}`}
                        </div>
                      </div>
                      {g.is_live && <Badge className="bg-red-500/20 text-red-400 border border-red-500/40 animate-pulse">LIVE</Badge>}
                      {g.is_final && <Badge variant="outline" className="border-brand-lime text-brand-lime">Final</Badge>}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">{g.away_abbr}</div>
                        <div className="font-mono font-bold text-2xl text-white">{g.away_score ?? '—'}</div>
                        {g.away_sog != null && <div className="text-[10px] text-muted-foreground">{g.away_sog} SOG</div>}
                      </div>
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">{g.home_abbr}</div>
                        <div className="font-mono font-bold text-2xl text-white">{g.home_score ?? '—'}</div>
                        {g.home_sog != null && <div className="text-[10px] text-muted-foreground">{g.home_sog} SOG</div>}
                      </div>
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">Moneyline</div>
                        <div className="font-mono font-bold text-brand-cyan">
                          {g.odds?.away ?? '—'} / {g.odds?.home ?? '—'}
                        </div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">GF/G — GA/G</div>
                        <div className="font-mono text-xs text-white mt-1">{g.away_abbr}: {g.away_stats?.gf_pg ?? '—'} / {g.away_stats?.ga_pg ?? '—'}</div>
                        <div className="font-mono text-xs text-white">{g.home_abbr}: {g.home_stats?.gf_pg ?? '—'} / {g.home_stats?.ga_pg ?? '—'}</div>
                      </div>
                    </div>

                    <div className="overflow-x-auto mb-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                            <th className="text-left py-1.5">Team</th>
                            <th className="text-center py-1.5">Record</th>
                            <th className="text-center py-1.5">Last 10</th>
                            <th className="text-center py-1.5 hidden sm:table-cell">Home</th>
                            <th className="text-center py-1.5 hidden sm:table-cell">Road</th>
                            <th className="text-center py-1.5">Pts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[['away', g.away_abbr, g.away_stats], ['home', g.home_abbr, g.home_stats]].map(([side, abbr, ts]) => (
                            <tr key={side} className="border-b border-[#1e3a5f]/40">
                              <td className="py-1.5 text-white font-medium">{ts?.name || abbr}</td>
                              <td className="text-center font-mono text-white">{ts?.record ?? '—'}</td>
                              <td className="text-center font-mono text-white">{ts?.l10 ?? '—'}</td>
                              <td className="text-center font-mono text-muted-foreground hidden sm:table-cell">{ts?.home_record ?? '—'}</td>
                              <td className="text-center font-mono text-muted-foreground hidden sm:table-cell">{ts?.road_record ?? '—'}</td>
                              <td className="text-center font-mono text-brand-lime">{ts?.points ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      {[['away', g.away_abbr, g.away_goalies], ['home', g.home_abbr, g.home_goalies]].map(([side, abbr, goalies]) => (
                        <div key={side} className="bg-[#0a1628] rounded p-3" data-testid={`nhl-goalies-${side}-${i}`}>
                          <div className="text-[10px] text-muted-foreground uppercase mb-2">{abbr} Goaltenders</div>
                          {(goalies || []).length === 0 ? <div className="text-xs text-muted-foreground">—</div> : (goalies || []).map((gk, j) => (
                            <div key={j} className="flex items-center justify-between text-xs py-0.5">
                              <span className="text-white font-medium">{gk.name}</span>
                              <span className="font-mono text-muted-foreground">{gk.record} • {gk.save_pct} SV% • {gk.gaa} GAA</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    {(g.h2h || []).length > 0 && (
                      <div className="bg-[#0a1628] rounded p-3">
                        <div className="text-[10px] text-muted-foreground uppercase mb-2">Head-to-Head This Season</div>
                        <div className="flex flex-wrap gap-2">
                          {g.h2h.map((m, j) => (
                            <span key={j} className="font-mono text-xs text-white bg-[#112240] px-2 py-1 rounded">
                              {m.date}: {m.away} {m.away_score} @ {m.home} {m.home_score}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NhlMatchups;
